import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts/dist/react-apexcharts.esm.js";
import { DateTime } from "luxon";
import { useGetStatusPeriods } from "../../../commons/hooks/useGetStatusPeriods";
import { Skeleton } from "../../../commons/components/skeleton/Skeleton";

const formatDate = (date) => {
  return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
};

const SimpleBarGraph = ({ idPlanta, column, fecha, fechaFin }) => {
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState({});

  const { periods, loading, error, fetchStatusPeriods } = useGetStatusPeriods();

  // Fetch data when component mounts or parameters change
  useEffect(() => {
    if (idPlanta && column && fecha && fechaFin) {
      fetchStatusPeriods({
        idPlanta,
        column,
        fechaInicio: formatDate(fecha),
        fechaFin: formatDate(fechaFin)
      });
    }
  }, [idPlanta, column, fecha, fechaFin, fetchStatusPeriods]);

  useEffect(() => {
    if (!periods || periods.length === 0) {
      return;
    }

    const now = new Date().getTime();

    // Ajustar timestamps para plantas 161 y 191 (restar 2 horas)
    const needsTimezoneAdjustment = idPlanta === 161 || idPlanta === 191;
    const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 horas en milisegundos

    // Convertir periodos a formato del gráfico
    const processedData = periods.map((period, index) => {
      const stateInfo = getStateInfo(period.estado);
      let startTime = new Date(period.inicio).getTime();
      let endTime = new Date(period.fin).getTime();

      // Aplicar ajuste de zona horaria si es necesario
      if (needsTimezoneAdjustment) {
        startTime -= twoHoursInMs;
        endTime -= twoHoursInMs;
      }

      // Si es el último bloque y termina después de la hora actual,
      // ajustarlo a la hora actual
      if (index === periods.length - 1 && endTime > now) {
        endTime = now;
      }

      return {
        x: stateInfo.label,
        y: [startTime, endTime],
        fillColor: stateInfo.color
      };
    });

    // Usar el primer dato como inicio y la hora actual como fin
    const firstPeriodStart = processedData[0].y[0];
    const lastPeriodEnd = processedData[processedData.length - 1].y[1];
    const chartEnd = Math.min(now, lastPeriodEnd);

    setSeries([{ data: processedData }]);

    setOptions({
      chart: {
        type: "rangeBar",
        animations: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: false,
          dataLabels: {
            hideOverflowingLabels: false
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false
        },
        min: firstPeriodStart,
        max: chartEnd
      },
      yaxis: {
        show: true
      },
      grid: {
        row: {
          colors: ["#f3f4f5", "#fff"],
          opacity: 1
        }
      },
      tooltip: {
        x: {
          format: "dd/MM/yy HH:mm"
        }
      },
      legend: {
        show: true,
        position: "top"
      }
    });
  }, [periods, idPlanta]);

  const getStateInfo = (state) => {
    if (state === 0) {
      return {
        label: "En Funcionamiento",
        color: "#00E396" // Verde
      };
    }
    // state === 1
    return {
      label: "Falla",
      color: "#FF4560" // Rojo
    };
  };

  if (loading) {
    return <Skeleton style={{ height: "200px", borderRadius: "8px" }} />;
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#dc3545" }}>
        Error al cargar los datos
      </div>
    );
  }

  if (!periods || periods.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#6c757d" }}>
        No se han encontrado registros en las fechas seleccionadas
      </div>
    );
  }

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="rangeBar"
      height="200"
    />
  );
};

export default SimpleBarGraph;
